import { CalibrationDisplaySchema } from "../types";

/**
 * Tiltmeter calibration display schema.
 *
 * Tiltmeter calibrations are submitted by NexaWave Device Setup after
 * 6-face gravity calibration + 13-angle tilt test (AT+SIN?).
 * There is no manual input form — inputFieldConfig is intentionally omitted.
 */
export const TiltmeterSchema: CalibrationDisplaySchema = {
  sensorType: "mems",
  unit: "°",

  certificateFormConfig: {
    title: "Tiltmeter Certificate Details",
    fields: [
      {
        key: "date",
        label: "Date",
        type: "date",
        placeholder: "DD.MM.YYYY",
      },
      {
        key: "temperature",
        label: "Temperature",
        type: "text",
        placeholder: "e.g., 26 °C",
        unit: "°C",
      },
    ],
  },

  inputData: {
    sections: [
      {
        title: "Test Configuration",
        bgColor: "bg-blue-50",
        group: "setup",
        fields: [
          { key: "g_range", label: "MEMS Range", unit: "g" },
          { key: "temperature", label: "Temperature", unit: "°C" },
          { key: "humidity", label: "Humidity", unit: "%" },
          { key: "firmware_version", label: "Firmware Version", unit: "" },
        ],
      },
      {
        title: "Calibration Coefficients",
        bgColor: "bg-indigo-50",
        group: "setup",
        fields: [
          {
            key: "calibration_coefficients.offset_x",
            label: "Offset X",
            unit: "g",
          },
          {
            key: "calibration_coefficients.offset_y",
            label: "Offset Y",
            unit: "g",
          },
          {
            key: "calibration_coefficients.offset_z",
            label: "Offset Z",
            unit: "g",
          },
          {
            key: "calibration_coefficients.gauge_factor_x",
            label: "Gauge Factor X",
            unit: "",
          },
          {
            key: "calibration_coefficients.gauge_factor_y",
            label: "Gauge Factor Y",
            unit: "",
          },
          {
            key: "calibration_coefficients.gauge_factor_z",
            label: "Gauge Factor Z",
            unit: "",
          },
        ],
        condition: (data: unknown) =>
          typeof data === "object" &&
          data !== null &&
          "calibration_coefficients" in data,
      },
    ],

    tables: [
      {
        title: "13-Angle Tilt Test (Raw SIN readings)",
        bgColor: "bg-green-50",
        group: "measurement",
        dataKey: "test_report.tilt_readings",
        columns: [
          { key: "angle_deg", label: "Angle", unit: "°" },
          { key: "sin_x", label: "Sin X", unit: "", className: "font-mono" },
          { key: "sin_y", label: "Sin Y", unit: "", className: "font-mono" },
          { key: "sin_z", label: "Sin Z", unit: "", className: "font-mono" },
        ],
        // No `condition` here — dataKey resolution above already hides the
        // table when there's no data.
      },
    ],
  },

  outputData: {
    sections: [
      {
        title: "Summary",
        bgColor: "bg-green-50",
        group: "result",
        fields: [
          { key: "calibration_status", label: "Calibration Status", unit: "" },
          { key: "g_range", label: "MEMS Range", unit: "g" },
          {
            key: "measurement_range",
            label: "Tilt Range",
            unit: "°",
          },
          {
            key: "max_nonconformance_x",
            label: "Max NC X",
            unit: "% fs",
          },
          {
            key: "max_nonconformance_y",
            label: "Max NC Y",
            unit: "% fs",
          },
          {
            key: "max_nonconformance_z",
            label: "Max NC Z",
            unit: "% fs",
          },
          {
            key: "max_nonconformance_percent_fs",
            label: "Max NC (overall)",
            unit: "% fs",
          },
          { key: "temperature", label: "Temperature", unit: "°C" },
          { key: "humidity", label: "Humidity", unit: "%" },
          { key: "firmware_version", label: "Firmware Version", unit: "" },
        ],
      },
      {
        title: "Calibration Coefficients",
        bgColor: "bg-indigo-50",
        group: "result",
        fields: [
          { key: "calibration_coefficients.offset_x", label: "Offset X", unit: "g" },
          { key: "calibration_coefficients.offset_y", label: "Offset Y", unit: "g" },
          { key: "calibration_coefficients.offset_z", label: "Offset Z", unit: "g" },
          {
            key: "calibration_coefficients.gauge_factor_x",
            label: "Gauge Factor X",
            unit: "",
          },
          {
            key: "calibration_coefficients.gauge_factor_y",
            label: "Gauge Factor Y",
            unit: "",
          },
          {
            key: "calibration_coefficients.gauge_factor_z",
            label: "Gauge Factor Z",
            unit: "",
          },
        ],
        condition: (data: unknown) =>
          typeof data === "object" &&
          data !== null &&
          "calibration_coefficients" in data,
      },
    ],

    tables: [
      {
        title: "Tilt Test Results (computed)",
        bgColor: "bg-blue-50",
        group: "analysis",
        dataKey: "tilt_results",
        columns: [
          { key: "angle_deg", label: "Angle", unit: "°" },
          { key: "reference_sin", label: "Ref sin", unit: "", className: "font-mono" },
          { key: "sin_x", label: "Obs Sin X", unit: "", className: "font-mono" },
          { key: "sin_y", label: "Obs Sin Y", unit: "", className: "font-mono" },
          { key: "sin_z", label: "Obs Sin Z", unit: "", className: "font-mono" },
          {
            key: "offset_corrected_x",
            label: "Corr X",
            unit: "",
            className: "font-mono",
          },
          {
            key: "offset_corrected_y",
            label: "Corr Y",
            unit: "",
            className: "font-mono",
          },
          {
            key: "offset_corrected_z",
            label: "Corr Z",
            unit: "",
            className: "font-mono",
          },
          {
            key: "nonconformance_x",
            label: "NC X",
            unit: "% fs",
            className: "font-mono",
          },
          {
            key: "nonconformance_y",
            label: "NC Y",
            unit: "% fs",
            className: "font-mono",
          },
          {
            key: "nonconformance_z",
            label: "NC Z",
            unit: "% fs",
            className: "font-mono",
          },
        ],
        condition: (data: unknown) =>
          typeof data === "object" &&
          data !== null &&
          "tilt_results" in data,
      },
    ],
  },
};
